/** @jsx jsx */
import { Button, Grid, isBlank, Spacer, TextGroupSmall, useAsyncEffect } from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { P } from "@cd2/text/build/es6"
import { assert } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import {
  egsBranchId,
  fortusEwBranchId,
  londonBranchId,
  sspBranchId,
} from "../../../../shared/constants"
import { themeColors } from "../../../Components/EOSThemeProvider"
import { useGlobalState } from "../../../Components/GlobalStateProvider"
import { FloatingBox } from "../../../Components/Misc/FloatingBox"
import { highlight, ProductSelect } from "../../../Components/ProductSelect"
import { QuickToolTipWrapper } from "../../../Components/QuickToolTipWrapper"
import { ProductGroupSelect } from "../../../Components/SelectLists/ProductGroupSelectList"
import { SupplierSelect } from "../../../Components/SelectLists/SupplierSelectList"
import { useSiteAlerts } from "../../../hooks/useSiteAlerts"
import { buildRoute, eRoutes } from "../../../routes"
import { isProductExported } from "../../../stories/quoteOrders/quoteOrderUtils"
import { IRecord } from "../../../types/schema"
import { uniq } from "../../../utils/utils"
import { InfoBanner } from "../../AlertBanners"

interface IData {
  stock_code: string
  name: string
  body: string
  product_group: string
  manufacturer: IRecord<"manufacturers", "name">
  manufacturerCode: string
  supplier: string
  supplier_part_number: string
  cost_price_pennies: number | null
  active: boolean
  product_master: IRecord<"product_masters", "name" | "stock_code">
  exportOnSave?: boolean
  warehouse?: IRecord<"warehouses", "name">
  orderRequestAlternativeProduct?: IRecord<"products", "stock_code" | "body">
  few_supplier?: string
  few_product_group?: string
  barcode?: string
  minimum_selling_price_pennies?: number | null
  hidden?: boolean
}

export type IProductFormProduct = IRecord<
  "products",
  | "active"
  | "stock_code"
  | "name"
  | "body"
  | "product_group"
  | "manufacturerCode"
  | "preferred_supplier"
  | "cost_price_pennies"
  | "minimum_selling_price_pennies"
  | "barcode"
> & {
  manufacturer: IRecord<"manufacturers", "name">
  product_master: IRecord<"product_masters", "name" | "stock_code" | "hidden">
  supplier_stock_codes: IRecord<"supplier_stock_codes", "stock_code" | "supplier">[]
  warehouse?: IRecord<"warehouses", "name">
  orderRequestAlternativeProduct?: IRecord<"products", "name" | "body" | "stock_code">
  exportOnSave?: boolean
}

function getValueOrSource(pmValue: any, initialDataValue: any, formValue: any) {
  if (!isBlank(pmValue)) {
    return pmValue
  }
  if (!isBlank(initialDataValue)) {
    return initialDataValue
  }
  return formValue
}

function useHubSync(branchId?: string, fewBranchId?: string) {
  return {
    branchId: branchId || "",
    disabled: sspBranchId === branchId || egsBranchId === branchId,
    isFewBranch: branchId === fewBranchId,
  }
}

export function ProductForm({
  data,
  onSubmit,
  isNew,
  canEditProductMaster,
  disabledEditProductMasterReason,
  onCancel,
  autoLoadProductMaster,
  branch,
}: {
  data?: Partial<IProductFormProduct>
  onSubmit?(data: IData): Promise<void> | void
  isNew?: boolean
  canEditStockCodeUnexported?: boolean
  canEditProductMaster?: boolean
  disabledEditProductMasterReason?: string
  onCancel?: () => void
  autoLoadProductMaster?: boolean
  branch?: { id: string; name: string }
}) {
  const alerts = useSiteAlerts()
  const { currentUser } = useGlobalState()
  const skeem = window.skeem
  const effectiveBranch = branch ?? currentUser.branch
  const effectiveBranchId = effectiveBranch.id!
  const isLondonBranch = effectiveBranchId === londonBranchId
  const formRef = useRef<FormState | null>(null)
  const [loadedProductMaster, setLoadedProductMaster] = useState<IMatchedProductMaster | null>(null)
  const [defaultOrBranch, setDefaultOrBranch] = useState<IRecord<
    "orderRequestBranches",
    "targetBranch"
  > | null>(null)
  useAsyncEffect(async () => {
    if (!currentUser?.id) {
      return
    }
    const orBranch = await skeem.fetch2<IRecord<"orderRequestBranches", "targetBranch">>({
      first: {
        name: "orderRequestBranches",
        attributes: ["targetBranch"],
        matches: { sourceBranch: currentUser.branch.id, default: true },
      },
    })
    setDefaultOrBranch(orBranch)
  }, [currentUser?.id])

  function productMasterHasExportedProduct(productMaster: IMatchedProductMaster | null) {
    return productMaster?.products.some(
      p =>
        isProductExported(p) ||
        p.sage_export_status === "pendingExport" ||
        p.sage_export_status === "in_progress",
    )
  }

  function editStockCodeDisabled(productMaster: IMatchedProductMaster | null) {
    if (!onSubmit) {
      return true
    }
    const hasExported = productMasterHasExportedProduct(productMaster)
    if (hasExported) {
      return "This product or a product associated through the product master has already been exported to Sage."
    }
    return false
  }

  async function loadProductMaster(productMasterId: string) {
    const productMaster = await productFormLoadProductMasterData(skeem, effectiveBranch, {
      id: productMasterId,
    })
    console.log("productMaster", productMaster)
    setLoadedProductMaster(productMaster)
  }

  useAsyncEffect(() => {
    if (data?.product_master?.id && !isLondonBranch) {
      loadProductMaster(data.product_master.id)
    }
  }, [data?.product_master?.id])

  const [fieldReloadKey, setFieldReloadKey] = useState(0)

  async function loadProductMasters(query: string) {
    return skeem.fetch2<
      (IRecord<"product_masters", "stock_code" | "name"> & { manufacturerCodes: string[] })[]
    >({
      model: {
        name: "product_masters",
        attributes: [
          "hidden",
          "name",
          "stock_code",
          { as: "manufacturerCodes", value: { path: ["products", "manufacturerCode"] } },
        ],
        filter: {
          and: [
            {
              or: [
                { matches: { stock_code: { like: query } } },
                { matches: { name: { like: query } } },
                {
                  exists: {
                    path: [
                      {
                        name: "products",
                        filter: { matches: { manufacturerCode: { like: query } } },
                      },
                    ],
                  },
                },
              ],
            },
            {
              not: {
                exists: {
                  path: [
                    "products",
                    {
                      name: "branch",
                      filter: { matches: { id: effectiveBranchId } },
                    },
                  ],
                },
              },
            },
          ],
        },
        paginate: {
          page: 1,
          size: 30,
        },
      },
    })
  }

  async function loadManufacturers(query: string) {
    return skeem.fetch2<IRecord<"manufacturers", "name">[]>({
      model: {
        name: "manufacturers",
        attributes: ["name"],
        filter: {
          or: [{ matches: { name: { like: query } } }],
        },
        sort: { by: "name", direction: "ASC" },
      },
    })
  }

  async function loadWarehouses(query: string) {
    return skeem.fetch2<IRecord<"warehouses", "name">[]>({
      model: {
        name: "warehouses",
        attributes: ["name"],
        filter: {
          and: [
            {
              or: [{ matches: { name: { like: query } } }],
            },
            { matches: { branch: effectiveBranchId } },
          ],
        },
        sort: { by: "name", direction: "ASC" },
      },
    })
  }

  async function handleSubmit(data: IData) {
    try {
      const productBranch = effectiveBranch
      if (isNew) {
        const warehouse = await skeem.fetch2<IRecord<"warehouses", "branch">>({
          first: {
            name: "warehouses",
            attributes: ["branch"],
            matches: { id: data.warehouse?.id },
          },
        })
        assert(
          productBranch.id == warehouse.branch?.id,
          "Warehouse must belong to the product branch",
        )
      }
      const productGroup = await skeem.fetch2<IRecord<"product_groups", "branch">>({
        first: {
          name: "product_groups",
          attributes: ["branch"],
          matches: { id: data.product_group },
        },
      })
      assert(
        productBranch.id == productGroup.branch?.id,
        "Product group must belong to the product branch",
      )

      await onSubmit?.(data)
    } catch (e) {
      console.error(e)
      alerts.genericError(e.message)
      return
    }
  }

  function formatInitialData(data: Partial<IProductFormProduct>): Partial<IData> {
    const supplierStockCode = data.supplier_stock_codes?.find(
      code => code.supplier?.id === data.preferred_supplier?.id,
    )
    return {
      body: data.body,
      manufacturer: data.manufacturer,
      manufacturerCode: data.manufacturerCode,
      name: data.name,
      product_group: data.product_group?.id,
      product_master: data.product_master,
      cost_price_pennies: data.cost_price_pennies,
      stock_code: data.stock_code,
      supplier: data.preferred_supplier?.id,
      supplier_part_number: supplierStockCode?.stock_code,
      warehouse: data.warehouse,
      minimum_selling_price_pennies: data.minimum_selling_price_pennies,
      orderRequestAlternativeProduct: data.orderRequestAlternativeProduct,
      barcode: data.barcode,
      exportOnSave: currentUser.canExportNewProducts ? data.exportOnSave ?? false : false,
      hidden: true,
    }
  }

  function stripNull<Data extends Record<string, any>>(data: Data) {
    const clone = { ...data }
    Object.keys(clone).forEach(key => {
      if (clone[key] === null) {
        delete clone[key]
      } else if (typeof clone[key] === "object") {
        clone[key as keyof Data] = stripNull(clone[key])
      } else if (Array.isArray(clone[key])) {
        clone[key as keyof Data] = clone[key].filter((v: any) => v !== null)
      }
    })
    return clone
  }

  async function checkProductCodeForMaster() {
    if (isLondonBranch) {
      return
    }
    const stockCodeField = formRef.current?.getField("stock_code")
    const stockCode = stockCodeField?.getValue()
    const productMasterField = formRef.current?.getField("product_master")
    const productMaster = productMasterField?.getValue()

    if (stockCode && !productMaster) {
      const downcaseStockCode = stockCode.toLowerCase()
      const matchingPm = await skeem.fetch2<IRecord<"product_masters", "name" | "stock_code">>({
        first: {
          name: "product_masters",
          attributes: ["name", "stock_code"],
          filter: { eq: [{ lowercase: { attr: "stock_code" } }, { value: downcaseStockCode }] },
        },
      })
      if (matchingPm) {
        productMasterField?.setValue(matchingPm)
        const loadedPm = await productFormLoadProductMasterData(skeem, effectiveBranch, matchingPm)
        setLoadedProductMaster(loadedPm)
        stockCodeField?.setValue(matchingPm.stock_code)
      }
    }
  }

  return (
    <Form2.Form xRef={formRef} onSubmit={handleSubmit} initialData={formatInitialData(data ?? {})}>
      <ProductValidations
        id={data?.id}
        branch={{ id: effectiveBranchId, name: effectiveBranch.name }}
      />
      <ValidateDuplicateProductMaster isLondonBranch={isLondonBranch} />
      <Form2.WithForm
        subscribe={{ dataChange: true, submitting: true }}
        render={form => {
          const masterField = form.getField("product_master")

          useEffect(() => {
            if (autoLoadProductMaster && data?.product_master?.id && !isLondonBranch) {
              handleProductMasterChange(data.product_master)
            }
          }, [])

          async function handleProductMasterChange(
            productMaster: IProductFormProduct["product_master"],
          ) {
            if (productMaster) {
              const record = await productFormLoadProductMasterData(
                skeem,
                effectiveBranch,
                productMaster,
              )
              console.log("record", record)
              setLoadedProductMaster(record)
              const keys = [
                "body",
                "manufacturer",
                "manufacturerCode",
                "product_group",
                "product_master",
                "stock_code",
                "supplier",
                "supplier_part_number",
                "warehouse",
              ]
              const strippedRecord = stripNull(record)
              const itemData = keys.reduce((acc, key) => {
                acc[key] = getValueOrSource(
                  strippedRecord[key as keyof typeof record],
                  data?.[key as keyof typeof data],
                  isNew ? null : form.getData()[key],
                )
                ;["supplier_part_number", "manufacturerCode", "body"].forEach(k => {
                  if (acc[k] === null) {
                    acc[k] = ""
                  }
                })
                return acc
              }, {} as Record<string, any>)
              form.setPartialData(itemData)
            } else {
              form.setPartialData({ product_master: undefined })
              setLoadedProductMaster(null)
            }
            setFieldReloadKey(k => k + 1)
          }

          return (
            <React.Fragment>
              {!isLondonBranch && (
                <FloatingBox
                  header={
                    <TextGroupSmall
                      heading="Product Master"
                      body="Copy product information from a product on another branches Sage"
                    />
                  }
                >
                  <Fields.AsyncSelect<
                    IProductFormProduct["product_master"] & { manufacturerCodes?: string[] }
                  >
                    field="product_master"
                    label="Product Master"
                    loadOptions={loadProductMasters}
                    onChange={handleProductMasterChange}
                    value={masterField.getValue()}
                    getOptionLabel={opt => opt.name}
                    getOptionValue={opt => opt.id}
                    formatOptionLabel={(opt, data) => (
                      <div>
                        <P blockSpacing={0} size="small">
                          {highlight(opt.stock_code, data.inputValue)}{" "}
                          {opt.manufacturerCodes && opt.manufacturerCodes.length > 0 ? (
                            <React.Fragment>
                              {"- "}
                              <span>
                                (
                                {highlight(
                                  uniq(
                                    opt.manufacturerCodes.filter(code => code.trim().length > 0),
                                  ).join(", "),
                                  data.inputValue,
                                )}
                                )
                              </span>
                            </React.Fragment>
                          ) : (
                            ""
                          )}
                        </P>
                        <P blockSpacing={0}>{highlight(opt.name, data.inputValue)}</P>
                      </div>
                    )}
                    disabled={!canEditProductMaster}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue.length === 0 ? "Type to search..." : "No results"
                    }
                    cacheOptions
                    isClearable
                  />
                </FloatingBox>
              )}

              {disabledEditProductMasterReason && !isLondonBranch && (
                <React.Fragment>
                  <InfoBanner message={disabledEditProductMasterReason} />
                </React.Fragment>
              )}
              <div>
                <Grid gap={8} columns={[{ fr: 1 }, { fr: 2 }]}>
                  <Form2InputWrapper
                    field="stock_code"
                    productMaster={loadedProductMaster}
                    disabled={() => !!editStockCodeDisabled(loadedProductMaster)}
                  >
                    {({ isDisabled }) => (
                      <QuickToolTipWrapper
                        content={editStockCodeDisabled(loadedProductMaster)}
                        showToolTip={!!editStockCodeDisabled(loadedProductMaster)}
                        toolTipProps={{
                          width: "100%",
                        }}
                      >
                        <Form2.Text
                          field="stock_code"
                          label="Stock Code"
                          disabled={isDisabled}
                          validations={{ presence: true, maxLength: 30 }}
                          inputProps={{ onBlur: checkProductCodeForMaster }}
                        />
                      </QuickToolTipWrapper>
                    )}
                  </Form2InputWrapper>
                  <Form2InputWrapper
                    field="body"
                    productMaster={loadedProductMaster}
                    disabled={!onSubmit}
                  >
                    <Form2.Text
                      field="body"
                      label="Name"
                      validations={{ presence: true, maxLength: 60 }}
                    />
                  </Form2InputWrapper>
                </Grid>
                <Form2.WithField
                  field="stock_code_validation_data"
                  subscribe={{ errorChange: true }}
                  render={field => {
                    const errorData = field.getError() as
                      | undefined
                      | { stock_code: string; id: string }[]
                    if (errorData) {
                      return (
                        <div
                          css={css`
                            a {
                              border-bottom: 1px dotted ${themeColors.grey};
                            }
                          `}
                        >
                          {errorData.map((item, i) => (
                            <P key={item.id}>
                              <Link
                                target="_blank"
                                to={buildRoute(eRoutes["/stock/products/:id"], { id: item.id })}
                              >
                                {i + 1}. View product with matching stock code
                              </Link>
                            </P>
                          ))}
                        </div>
                      )
                    }
                    return null
                  }}
                />
              </div>
              <ProductGroupAndFewProductGroupSelect
                hasOnSubmit={!!onSubmit}
                isNew={isNew}
                loadedProductMaster={loadedProductMaster}
                fewBranchId={fortusEwBranchId}
                branchId={effectiveBranchId}
              />

              <ValidateProductGroup />
              <BarcodeField />
              <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
                <Form2InputWrapper
                  field="manufacturer"
                  compare={(value, pm) => value?.id === pm?.manufacturer?.id}
                  getResetValue={pm => pm?.manufacturer}
                  productMaster={loadedProductMaster}
                  disabled={!onSubmit}
                >
                  <Form2.AsyncSelect
                    field="manufacturer"
                    label="Manufacturer"
                    loadOptions={loadManufacturers}
                    getOptionLabel={opt => opt.name}
                    getOptionValue={opt => opt.id}
                    validations={{ presence: true }}
                    cacheOptions
                    isClearable
                  />
                </Form2InputWrapper>
                <Form2InputWrapper
                  field="manufacturerCode"
                  productMaster={loadedProductMaster}
                  disabled={!onSubmit}
                >
                  <Form2.Text
                    field="manufacturerCode"
                    label="Manufacturer Part Number"
                    disabled={!onSubmit}
                    validations={{ presence: true, maxLength: 40 }}
                  />
                </Form2InputWrapper>
              </Grid>
              <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
                <SupplierAndFewSupplierSelect
                  loadedProductMaster={loadedProductMaster}
                  hasOnSubmit={!!onSubmit}
                  isNew={isNew}
                  fewBranchId={fortusEwBranchId}
                  branchId={effectiveBranchId}
                />
                <Form2InputWrapper
                  field="supplier_part_number"
                  productMaster={loadedProductMaster}
                  disabled={!onSubmit}
                >
                  <Form2.Text
                    field="supplier_part_number"
                    label="Supplier Stock Code"
                    disabled={!onSubmit}
                    validations={{ presence: true, maxLength: 40 }}
                  />
                </Form2InputWrapper>
              </Grid>
              {isNew && (
                <Form2.AsyncSelect
                  field="warehouse"
                  loadOptions={loadWarehouses}
                  getOptionLabel={opt => opt.name}
                  getOptionValue={opt => opt.id}
                  validations={{ presence: true }}
                  cacheOptions
                />
              )}

              <Grid columns={[{ fr: 1 }, { fr: 1 }]} gap={8}>
                <Form2InputWrapper
                  field="cost_price_pennies"
                  productMaster={loadedProductMaster}
                  disabled={!onSubmit}
                  getResetValue={pm => pm.cost_price_pennies}
                  reloadField={() => setFieldReloadKey(k => k + 1)}
                >
                  <Form2.Money
                    key={fieldReloadKey}
                    field="cost_price_pennies"
                    label="Cost Price"
                    disabled={!onSubmit}
                    validations={{ presence: true }}
                    asPennies
                  />
                </Form2InputWrapper>

                <Form2.Money
                  key={fieldReloadKey}
                  field="minimum_selling_price_pennies"
                  label="Minimum Selling Price"
                  disabled={!onSubmit}
                  asPennies
                />
              </Grid>

              <Form2.WithField<{ id: string } | undefined>
                field="orderRequestAlternativeProduct"
                render={field => (
                  <ProductSelect
                    label="OR/PO Alternative Product"
                    onChange={field.setValue}
                    value={field.getValue()?.id}
                    branchId={defaultOrBranch?.targetBranch?.id}
                    disabled={!defaultOrBranch}
                    isClearable
                    hideStock
                  />
                )}
              />
              <Grid columns={["max-content", "max-content"]} gap={8}>
                <Form2.Submit disabled={!onSubmit} />
                {onCancel && (
                  <Button onClick={onCancel} buttonColor="white">
                    Cancel
                  </Button>
                )}
              </Grid>
            </React.Fragment>
          )
        }}
      />
    </Form2.Form>
  )
}

function BarcodeField() {
  return <Form2.Text field="barcode" validations={{ maxLength: 60 }} />
}

function ValidateDuplicateProductMaster({ isLondonBranch }: { isLondonBranch: boolean }) {
  const skeem = window.skeem
  Form2.useFormSubmitLock(async state => {
    if (isLondonBranch) {
      return
    }
    const stockCodeField = state.getField("stock_code")
    const stockCode = stockCodeField?.getValue()

    const productMasterField = state.getField("product_master")
    const productMaster = productMasterField?.getValue()

    if (stockCode) {
      const downcaseStockCode = stockCode.toLowerCase()
      const matchingPm = await skeem.fetch2<IRecord<"product_masters", "name" | "stock_code">>({
        first: {
          name: "product_masters",
          attributes: ["name", "stock_code"],
          filter: { eq: [{ lowercase: { attr: "stock_code" } }, { value: downcaseStockCode }] },
        },
      })
      if (productMaster?.id !== matchingPm?.id) {
        if (matchingPm) {
          stockCodeField.setError(
            "This stock code belongs to an existing product master, but the product master was not selected.",
          )
        }
      }
    }
  })
  return null
}

function Form2InputWrapper({
  field,
  productMasterField,
  children,
  compare,
  getResetValue,
  reloadField,
  productMaster,
  disabled: disabled,
}: {
  field: string
  productMasterField?: string
  children:
    | React.ReactNode
    | ((props: { isDisabled?: boolean; formField: Form2.IFieldValue<any> }) => React.ReactNode)
  compare?: (value: any, pmValue: IMatchedProductMaster) => boolean
  getResetValue?(productMaster: IMatchedProductMaster): any
  reloadField?(): void
  productMaster?: IMatchedProductMaster | null
  disabled: boolean | ((state: any) => boolean)
}) {
  return (
    <Form2.WithForm
      subscribe={{ dataChange: true }}
      render={form => {
        const valueField = form.getField(field)
        const value = valueField.getValue()
        const isDisabled = typeof disabled === "function" ? disabled(value) : disabled
        let matched = false
        if (productMaster && value) {
          matched =
            compare?.(value, productMaster) ??
            productMaster[(productMasterField || field) as keyof typeof productMaster] === value
        }
        const resetValue = productMaster
          ? !getResetValue
            ? productMaster[(productMasterField || field) as keyof typeof productMaster]
            : getResetValue(productMaster)
          : null

        return (
          <Grid gap={4}>
            <div
              css={
                matched
                  ? css`
                      input {
                        background-color: ${themeColors.lightGreen};
                      }

                      [class*="-control"]:first-child {
                        background-color: ${themeColors.lightGreen};
                      }
                    `
                  : undefined
              }
            >
              {typeof children === "function"
                ? children({ isDisabled, formField: valueField })
                : children}
            </div>
            {resetValue && !matched && !isDisabled ? (
              <P blockSpacing={0} size="small">
                <a
                  onClick={() => {
                    form.getField(field).setValue(resetValue)
                    reloadField?.()
                  }}
                  css={css`
                    cursor: pointer;
                  `}
                >
                  set to product master value
                </a>
              </P>
            ) : (
              ""
            )}
          </Grid>
        )
      }}
    />
  )
}

function ValidateProductGroup() {
  Form2.useFormSubmitLock(state => {
    const pgField = state.getField("product_group")
    if (!pgField.getValue()) {
      pgField.setError("must be present")
      return false
    }

    pgField.setError(undefined)
    return
  })
  return null
}

function ProductValidations({
  id,
  branch,
}: {
  id?: string
  branch: { id: string; name?: string }
}) {
  const alerts = useSiteAlerts()
  const skeem = window.skeem
  const formState = Form2.useForm({ subscribe: { dataChange: true, submitting: true } })
  const stockCodeField = formState.getField("stock_code")

  const debounceRef = useRef<any | null>(null)

  stockCodeField.events.on(Form2.fieldEvents.change, () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      validateProductUnique()
    }, 500)
  })

  useAsyncEffect(async () => {
    await validateProductUnique()
  }, [])

  async function validateProductUnique() {
    const stockCodeFieldData = formState.getField("stock_code_validation_data")
    if (!stockCodeField.getValue()) {
      stockCodeFieldData.setError(undefined)
      stockCodeField.setError(undefined)
      return
    }
    const products = await skeem.fetch2<IRecord<"products", "stock_code">[]>({
      model: {
        name: "products",
        attributes: ["stock_code"],
        filter: {
          and: [
            { matches: { branch: branch.id } },
            {
              eq: [
                { lowercase: { trim: { attr: "stock_code" } } },
                { value: stockCodeField.getValue()?.trim().toLowerCase() },
              ],
            },
            id && { not: { matches: { id } } },
          ].filter(Boolean),
        },
      },
    })

    if (products.length > 0) {
      stockCodeField.setError("Must be unique for branch")
      stockCodeFieldData.setError(products)
      return false
    } else {
      stockCodeFieldData.setError(undefined)
      stockCodeField.setError(undefined)
    }
  }

  Form2.useFormSubmitLock(async () => {
    const result = await validateProductUnique()
    if (result === false) {
      alerts.genericError(
        `A product with this stock code already exists for ${branch.name ?? "this branch"}`,
      )
    }
    return result
  })

  return null
}

interface IMatchedProductMaster {
  product_master?: IRecord<"product_masters"> & { products: IRecord<"products", "sage_id">[] }
  stock_code?: string
  body: string | null
  product_group: string | null
  manufacturer: IRecord<"manufacturers", "name"> | null
  manufacturerCode: string | null
  supplier: string | null
  supplier_part_number: string | null
  cost_price_pennies: number | null
  products: IRecord<"products", "sage_id" | "sage_export_status">[]
}

export async function productFormLoadProductMasterData(
  skeem: typeof window.skeem,
  branch: { id?: string | null },
  productMaster: IRecord<"product_masters">,
): Promise<IMatchedProductMaster> {
  const productMasterData = await skeem.fetch2<
    IRecord<"product_masters", "name" | "stock_code" | "cost_price_pennies" | "hidden"> & {
      brand?: IRecord<"brands", "name">
      products: (IRecord<
        "products",
        "product_group" | "cost_price_pennies" | "sage_id" | "sage_export_status"
      > & {
        supplier?: IRecord<"suppliers", "name">[]
        manufacturer?: IRecord<"manufacturers", "name">
        supplier_stock_codes?: IRecord<"supplier_stock_codes", "stock_code" | "supplier">[]
      })[]
    }
  >({
    first: {
      name: "product_masters",
      attributes: [
        "hidden",
        "name",
        "stock_code",
        "cost_price_pennies",
        { name: "brand", attributes: ["name"] },
        {
          name: "products",
          attributes: [
            "sage_id",
            "sage_export_status",
            {
              as: "product_group",
              value: {
                first: {
                  path: [
                    "product_group",
                    "product_group_master",
                    {
                      name: "product_groups",
                      filter: { matches: { branch: branch.id } },
                    },
                  ],
                },
              },
            },
            "cost_price_pennies",
            { name: "manufacturer", attributes: ["name"] },
            {
              as: "supplier",
              value: {
                path: [
                  "preferred_supplier",
                  "supplier_master",
                  {
                    name: "suppliers",
                    attributes: ["name"],
                    filter: { matches: { branch: branch.id } },
                  },
                ],
              },
            },
            {
              name: "supplier_stock_codes",
              attributes: ["stock_code", "supplier"],
            },
          ],
        },
      ],
      matches: { id: productMaster.id },
    },
  })

  console.log(productMasterData)

  const formattedProducts = productMasterData.products.map(product => ({
    cost_price_pennies: product.cost_price_pennies,
    product_group: product.product_group?.id,
    supplier: product.supplier?.[0]?.id,
    supplier_part_number:
      product.supplier_stock_codes?.find(
        x => x.stock_code.toLowerCase() === productMasterData.stock_code.toLowerCase(),
      )?.stock_code ?? product.supplier_stock_codes?.[0]?.stock_code,

    manufacturer: product.manufacturer,
  }))

  // get the product with the most filled in values
  const product = formattedProducts?.reduce((acc, product) => {
    const accFilled = Object.values(acc).filter(Boolean).length
    const productFilled = Object.values(product).filter(Boolean).length
    return accFilled > productFilled ? acc : product
  }, {} as any)

  return {
    product_master: { ...productMaster, products: productMasterData.products },
    stock_code: productMasterData.stock_code ?? null,
    body: productMasterData?.name ?? null,
    product_group: product.product_group ?? null,
    manufacturer: product.manufacturer ?? null,
    manufacturerCode: productMasterData.stock_code ?? null,
    supplier: product.supplier ?? null,
    supplier_part_number: product.supplier_part_number ?? null,
    products: (productMasterData.products ?? []).map(x => ({
      id: x.id,
      sage_id: x.sage_id,
      sage_export_status: x.sage_export_status,
    })),
    cost_price_pennies:
      (productMasterData.cost_price_pennies || product.cost_price_pennies) ?? null,
  }
}

function SupplierAndFewSupplierSelect({
  isNew,
  loadedProductMaster,
  hasOnSubmit,
  fewBranchId,
  branchId,
}: {
  isNew?: boolean
  loadedProductMaster?: IMatchedProductMaster | null
  hasOnSubmit?: boolean
  fewBranchId?: string
  branchId?: string
}) {
  const hubSync = useHubSync(branchId, fewBranchId)

  const skeem = window.skeem

  const form = Form2.useForm({ subscribe: { dataChange: true, submitting: true } })
  const supplierField = form.getField("supplier")
  const fewField = form.getField("few_supplier")

  const [enableChangeFewSupplier, setEnableChangeFewSupplier] = useState(false)

  // - isFewBranch checks if current user is ON the FEW branch. skipping this additional selector if they are
  // - Check fewBranchId has been loaded (so this works in dev too)
  // - Supplier must be set for this select to show
  // - enableChangeFewSupplier is set to true when a supplier is selected, the current branch is NOT
  // FEW, and the supplier did not match an FEW supplier via the supplier master
  // - only show if record is new.
  const isShowingSelector = !!(
    !hubSync.isFewBranch &&
    fewBranchId &&
    supplierField.getValue() &&
    enableChangeFewSupplier &&
    isNew &&
    !hubSync.disabled
  )

  useEffect(() => {
    supplierField.events.on(Form2.fieldEvents.change, async () => {
      if (hubSync.isFewBranch || !isNew || hubSync.disabled) {
        setEnableChangeFewSupplier(false)
        fewField.setValue(null)
        return
      }
      if (supplierField.getValue()) {
        const supplier = await skeem.fetch2<
          IRecord<"suppliers"> & {
            supplier_master?: IRecord<"supplier_masters", "suppliers">
          }
        >({
          first: {
            name: "suppliers",
            attributes: [
              {
                name: "supplier_master",
                attributes: [
                  {
                    name: "suppliers",
                    filter: { matches: { branch: fewBranchId } },
                  },
                ],
              },
            ],
            matches: { id: supplierField.getValue() },
          },
        })
        console.log("LOAD", supplierField.getValue(), supplier)
        fewField.setValue(supplier.supplier_master?.suppliers?.[0]?.id)
        setEnableChangeFewSupplier(!supplier.supplier_master?.suppliers?.[0]?.id)
      } else {
        fewField.setValue(null)
        setEnableChangeFewSupplier(false)
      }
    })
  }, [])

  Form2.useFormSubmitLock(() => {
    let hasError = false
    if (!supplierField.getValue()) {
      supplierField.setError("must be present")
      hasError = true
    } else {
      supplierField.setError(undefined)
    }

    if (isShowingSelector && !fewField.getValue() && !hubSync.disabled) {
      fewField.setError("must be present")
      hasError = true
    } else {
      fewField.setError(undefined)
    }

    if (hasError) {
      return false
    }
    return
  })

  return (
    <div>
      <Form2InputWrapper
        field="supplier"
        productMaster={loadedProductMaster}
        disabled={!hasOnSubmit}
      >
        <SupplierSelect
          key={String(supplierField.getValue())}
          value={supplierField.getValue()}
          onChange={supplierField.setValue}
          disabled={!hasOnSubmit}
          error={supplierField.getError()}
          branchId={branchId}
          required
        />
      </Form2InputWrapper>
      {isShowingSelector && !hubSync.disabled && (
        <React.Fragment>
          <Spacer size={8} />
          <Form2.WithField<string>
            field="few_supplier"
            render={fewField => (
              <SupplierSelect
                label={
                  <Form2.FieldLabel
                    fieldName="Fortus Branch Supplier"
                    label={"Fortus Branch Supplier"}
                    required
                  />
                }
                value={fewField.getValue()}
                onChange={fewField.setValue}
                disabled={!hasOnSubmit}
                error={fewField.getError()}
                branchId={fewBranchId}
                required
              />
            )}
            subscribe
          />
        </React.Fragment>
      )}
    </div>
  )
}

function ProductGroupAndFewProductGroupSelect({
  isNew,
  loadedProductMaster,
  hasOnSubmit,
  fewBranchId,
  branchId,
}: {
  isNew?: boolean
  loadedProductMaster?: IMatchedProductMaster | null
  hasOnSubmit?: boolean
  fewBranchId?: string
  branchId?: string
}) {
  const hubSync = useHubSync(branchId, fewBranchId)
  const skeem = window.skeem
  const form = Form2.useForm({ subscribe: { dataChange: true, submitting: true } })
  const productGroupField = form.getField("product_group")
  const fewField = form.getField("few_product_group")

  const [enableChangeFewProductGroup, setEnableChangeFewProductGroup] = useState(false)

  // - isFewBranch checks if current user is ON the FEW branch. skipping this additional selector if they are
  // - Check fewBranchId has been loaded (so this works in dev too)
  // - Product Group must be set for this select to show
  // - enableChangeFewProductGroup is set to true when a supplier is selected, the current branch is
  // NOT FEW, and the supplier did not match an FEW supplier via the supplier master
  // - only show if record is new.
  const isShowingSelector = !!(
    !hubSync.isFewBranch &&
    fewBranchId &&
    productGroupField.getValue() &&
    enableChangeFewProductGroup &&
    isNew
  )

  useEffect(() => {
    productGroupField.events.on(Form2.fieldEvents.change, async () => {
      if (hubSync.isFewBranch || !isNew || hubSync.disabled) {
        setEnableChangeFewProductGroup(false)
        fewField.setValue(null)
        return
      }
      if (productGroupField.getValue()) {
        const productGroup = await skeem.fetch2<
          IRecord<"product_groups"> & {
            product_group_master?: IRecord<"product_group_masters", "product_groups">
          }
        >({
          first: {
            name: "product_groups",
            attributes: [
              {
                name: "product_group_master",
                attributes: [
                  {
                    name: "product_groups",
                    filter: { matches: { branch: fewBranchId } },
                  },
                ],
              },
            ],
            matches: { id: productGroupField.getValue() },
          },
        })
        console.log("LOAD", productGroupField.getValue(), productGroup)
        fewField.setValue(productGroup.product_group_master?.product_groups?.[0]?.id)
        setEnableChangeFewProductGroup(!productGroup.product_group_master?.product_groups?.[0]?.id)
      } else {
        fewField.setValue(null)
        setEnableChangeFewProductGroup(false)
      }
    })
  }, [])

  Form2.useFormSubmitLock(() => {
    let hasError = false
    if (!productGroupField.getValue()) {
      productGroupField.setError("must be present")
      hasError = true
    } else {
      productGroupField.setError(undefined)
    }

    if (isShowingSelector && !fewField.getValue() && !hubSync.disabled) {
      fewField.setError("must be present")
      hasError = true
    } else {
      fewField.setError(undefined)
    }

    if (hasError) {
      return false
    }
    return
  })

  return (
    <div>
      <Form2InputWrapper
        field="product_group"
        productMaster={loadedProductMaster}
        disabled={!hasOnSubmit}
      >
        <ProductGroupSelect
          key={String(productGroupField.getValue())}
          value={productGroupField.getValue()}
          onChange={productGroupField.setValue}
          disabled={!hasOnSubmit}
          error={productGroupField.getError()}
          branchId={branchId}
          required
        />
      </Form2InputWrapper>
      {isShowingSelector && !hubSync.disabled && (
        <React.Fragment>
          <Spacer size={8} />

          <ProductGroupSelect
            label={
              <Form2.FieldLabel
                fieldName="Fortus Branch Product Group"
                label={"Fortus Branch Product Group"}
                required
              />
            }
            value={fewField.getValue()}
            onChange={fewField.setValue}
            disabled={!hasOnSubmit}
            error={fewField.getError()}
            branchId={fewBranchId}
            required
          />
        </React.Fragment>
      )}
    </div>
  )
}
